import { getDistance, isPointWithinRadius } from 'geolib';

class GPSTracker {
  constructor() {
    this.currentLocation = null;
    this.accuracy = null;
    this.warehouseCoordinates = [];
    this.watchId = null;
    this.options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000
    };
  }

  // Get current GPS position
  async getCurrentPosition() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp
          };

          this.currentLocation = location;
          this.accuracy = position.coords.accuracy;

          resolve({
            success: true,
            location: location,
            message: 'Location obtained successfully'
          });
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please allow location access and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while retrieving location.';
              break;
          }

          reject({
            success: false,
            error: errorMessage,
            code: error.code
          });
        },
        this.options
      );
    });
  }

  // Watch position changes
  startWatching(callback) {
    if (!navigator.geolocation) {
      callback({
        success: false,
        error: 'Geolocation is not supported by this browser'
      });
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: position.timestamp
        };

        this.currentLocation = location;
        this.accuracy = position.coords.accuracy;

        callback({
          success: true,
          location: location
        });
      },
      (error) => {
        let errorMessage = 'Failed to watch location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        callback({
          success: false,
          error: errorMessage,
          code: error.code
        });
      },
      this.options
    );
  }

  // Stop watching position
  stopWatching() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  // Calculate distance between two points
  calculateDistance(point1, point2) {
    try {
      const distance = getDistance(
        { latitude: point1.latitude, longitude: point1.longitude },
        { latitude: point2.latitude, longitude: point2.longitude }
      );
      
      return {
        success: true,
        distance: distance, // in meters
        distanceKm: (distance / 1000).toFixed(2),
        distanceMiles: (distance * 0.000621371).toFixed(2)
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to calculate distance'
      };
    }
  }

  // Check if current location is within warehouse proximity
  checkProximity(warehouseLocations, maxDistance = 100) {
    if (!this.currentLocation) {
      return {
        success: false,
        error: 'Current location not available'
      };
    }

    const results = warehouseLocations.map(warehouse => {
      const distance = this.calculateDistance(this.currentLocation, warehouse.coordinates);
      
      if (!distance.success) {
        return {
          warehouseId: warehouse.id,
          warehouseName: warehouse.name,
          isWithinRange: false,
          distance: null,
          error: distance.error
        };
      }

      const isWithinRange = distance.distance <= (warehouse.radius || maxDistance);
      
      return {
        warehouseId: warehouse.id,
        warehouseName: warehouse.name,
        isWithinRange: isWithinRange,
        distance: distance.distance,
        distanceKm: distance.distanceKm,
        allowedRadius: warehouse.radius || maxDistance,
        coordinates: warehouse.coordinates
      };
    });

    const nearestWarehouse = results
      .filter(r => r.distance !== null)
      .sort((a, b) => a.distance - b.distance)[0];

    const isAtWarehouse = results.some(r => r.isWithinRange);

    return {
      success: true,
      isAtWarehouse: isAtWarehouse,
      nearestWarehouse: nearestWarehouse,
      allWarehouses: results,
      currentLocation: this.currentLocation,
      accuracy: this.accuracy
    };
  }

  // Validate GPS accuracy
  validateAccuracy(requiredAccuracy = 100) {
    if (!this.currentLocation || this.accuracy === null) {
      return {
        isValid: false,
        accuracy: null,
        requiredAccuracy: requiredAccuracy,
        message: 'Location not available'
      };
    }

    const isValid = this.accuracy <= requiredAccuracy;
    
    return {
      isValid: isValid,
      accuracy: this.accuracy,
      requiredAccuracy: requiredAccuracy,
      message: isValid 
        ? 'GPS accuracy is acceptable' 
        : `GPS accuracy (${this.accuracy}m) exceeds required accuracy (${requiredAccuracy}m)`
    };
  }

  // Detect potential GPS spoofing
  detectSpoofing() {
    if (!this.currentLocation) {
      return {
        isSuspicious: false,
        reasons: [],
        score: 0
      };
    }

    const suspiciousReasons = [];
    let suspicionScore = 0;

    // Check for unrealistic accuracy (too perfect)
    if (this.accuracy < 1) {
      suspiciousReasons.push('Unrealistically high GPS accuracy');
      suspicionScore += 30;
    }

    // Check for missing altitude data (common in spoofed locations)
    if (this.currentLocation.altitude === null || this.currentLocation.altitude === undefined) {
      suspiciousReasons.push('Missing altitude data');
      suspicionScore += 10;
    }

    // Check for zero speed when location changes (mock locations often have zero speed)
    if (this.currentLocation.speed === 0 && this.previousLocation) {
      const distance = this.calculateDistance(this.currentLocation, this.previousLocation);
      if (distance.success && distance.distance > 10) {
        suspiciousReasons.push('Zero speed with significant location change');
        suspicionScore += 20;
      }
    }

    // Check timestamp consistency
    const now = Date.now();
    const locationTime = this.currentLocation.timestamp;
    const timeDiff = Math.abs(now - locationTime);
    
    if (timeDiff > 60000) { // More than 1 minute difference
      suspiciousReasons.push('Location timestamp inconsistency');
      suspicionScore += 15;
    }

    // Store current location as previous for next check
    this.previousLocation = { ...this.currentLocation };

    return {
      isSuspicious: suspicionScore >= 30,
      reasons: suspiciousReasons,
      score: suspicionScore,
      confidence: Math.min(100, 100 - suspicionScore)
    };
  }

  // Get location metadata for validation
  getLocationMetadata() {
    return {
      timestamp: Date.now(),
      currentLocation: this.currentLocation,
      accuracy: this.accuracy,
      options: this.options,
      browserSupport: {
        geolocation: !!navigator.geolocation,
        permissions: !!navigator.permissions
      },
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        onLine: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled
      }
    };
  }

  // Set warehouse locations
  setWarehouseLocations(locations) {
    this.warehouseCoordinates = locations;
  }

  // Get current location
  getCurrentLocationData() {
    return {
      location: this.currentLocation,
      accuracy: this.accuracy,
      timestamp: this.currentLocation?.timestamp || null
    };
  }

  // Check browser support for geolocation
  static checkBrowserSupport() {
    return {
      geolocation: !!navigator.geolocation,
      permissions: !!navigator.permissions,
      isSecureContext: window.isSecureContext,
      isSupported: !!navigator.geolocation && window.isSecureContext
    };
  }

  // Request location permission
  static async requestPermission() {
    if (!navigator.permissions) {
      return {
        success: false,
        error: 'Permissions API not supported'
      };
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      
      return {
        success: true,
        state: permission.state,
        granted: permission.state === 'granted'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to check location permission'
      };
    }
  }
}

export default GPSTracker;
export { GPSTracker };
